<template>
  <DashboardLayout>
    <div class="space-y-6">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-secondary-900 dark:text-white">Performance Monitoring</h1>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Monitor application performance metrics and system health
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="refreshMetrics"
            class="px-3 py-2 text-sm bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 rounded-lg hover:bg-secondary-200 dark:hover:bg-secondary-600 transition-colors"
          >
            <ArrowPathIcon class="h-4 w-4 inline mr-1" />
            Refresh
          </button>
          <button
            @click="showInfo = true"
            class="p-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white rounded-full hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
          >
            <InformationCircleIcon class="h-6 w-6" />
          </button>
        </div>
      </div>

      <!-- System Health Status -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <HealthCard
          title="Database"
          :status="health.database"
          :responseTime="metrics.database_response_time"
        />
        <HealthCard
          title="Cache"
          :status="health.cache"
          :hitRate="metrics.cache_hit_rate"
        />
        <HealthCard
          title="Queue"
          :status="health.queue"
          :jobsProcessed="metrics.jobs_processed"
        />
        <HealthCard
          title="Storage"
          :status="health.storage"
          :usedSpace="metrics.storage_used"
        />
      </div>

      <!-- Response Time Metrics -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
            Response Time Analysis
          </h3>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Average</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ metrics.avg_response_time }}ms
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">P50 (Median)</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ metrics.p50_response_time }}ms
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">P95</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ metrics.p95_response_time }}ms
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">P99</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ metrics.p99_response_time }}ms
              </dd>
            </div>
          </div>

          <div v-if="slowestEndpoints.length > 0" class="mt-6">
            <h4 class="text-sm font-medium text-secondary-900 dark:text-white mb-4">Slowest Endpoints</h4>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                <thead class="bg-secondary-50 dark:bg-secondary-900/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase">
                      Endpoint
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase">
                      Avg Time
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase">
                      Requests
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-secondary-200 dark:divide-secondary-700">
                  <tr v-for="endpoint in slowestEndpoints" :key="endpoint.path">
                    <td class="px-4 py-3 text-sm text-secondary-900 dark:text-white">
                      {{ endpoint.method }} {{ endpoint.path }}
                    </td>
                    <td class="px-4 py-3 text-sm text-secondary-900 dark:text-white">
                      {{ endpoint.avg_time }}ms
                    </td>
                    <td class="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">
                      {{ formatNumber(endpoint.count) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Database Metrics -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
            Database Performance
          </h3>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Query Count</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ formatNumber(dbMetrics.query_count) }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Database Size</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ dbMetrics.database_size }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Connections</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ dbMetrics.active_connections }}
              </dd>
            </div>
          </div>

          <div v-if="slowQueries.length > 0" class="mt-6">
            <h4 class="text-sm font-medium text-secondary-900 dark:text-white mb-4">Slow Queries (&gt;1s)</h4>
            <div class="space-y-3">
              <div
                v-for="(query, index) in slowQueries"
                :key="index"
                class="bg-secondary-50 dark:bg-secondary-900/50 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs text-secondary-600 dark:text-secondary-400">Execution Time: {{ query.time }}ms</span>
                  <span class="text-xs text-secondary-600 dark:text-secondary-400">{{ query.count }} executions</span>
                </div>
                <code class="text-xs text-secondary-700 dark:text-secondary-300 font-mono block overflow-x-auto">{{ query.sql }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Queue and Cache Metrics -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Queue Stats -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
          <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
            <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
              Queue Status
            </h3>
          </div>
          <div class="p-6">
            <dl class="space-y-4">
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Jobs Processed Today</dt>
                <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                  {{ formatNumber(queueMetrics.jobs_processed) }}
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Failed Jobs</dt>
                <dd class="mt-1 text-2xl font-semibold text-danger-600 dark:text-danger-400">
                  {{ queueMetrics.failed_jobs }}
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Average Processing Time</dt>
                <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                  {{ queueMetrics.avg_processing_time }}s
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Queue Backlog</dt>
                <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                  {{ queueMetrics.backlog_count }} jobs
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <!-- Cache Stats -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
          <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
            <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
              Cache Performance
            </h3>
          </div>
          <div class="p-6">
            <dl class="space-y-4">
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Hit Rate</dt>
                <dd class="mt-1 text-2xl font-semibold text-success-600 dark:text-success-400">
                  {{ cacheMetrics.hit_rate }}%
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Miss Rate</dt>
                <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                  {{ cacheMetrics.miss_rate }}%
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Cache Size</dt>
                <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                  {{ cacheMetrics.cache_size }}
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Memory Usage</dt>
                <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                  {{ cacheMetrics.memory_usage }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <!-- Error Tracking -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
            Error Tracking
          </h3>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Error Rate</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ errorMetrics.error_rate }}%
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Total Errors (24h)</dt>
              <dd class="mt-1 text-2xl font-semibold text-danger-600 dark:text-danger-400">
                {{ errorMetrics.total_errors }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-secondary-500 dark:text-secondary-400">Unique Errors</dt>
              <dd class="mt-1 text-2xl font-semibold text-secondary-900 dark:text-white">
                {{ errorMetrics.unique_errors }}
              </dd>
            </div>
          </div>

          <div v-if="recentErrors.length > 0" class="mt-6">
            <h4 class="text-sm font-medium text-secondary-900 dark:text-white mb-4">Most Common Errors</h4>
            <div class="space-y-3">
              <div
                v-for="error in recentErrors"
                :key="error.id"
                class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <p class="text-sm font-medium text-danger-800 dark:text-danger-300">{{ error.message }}</p>
                    <p class="text-xs text-danger-600 dark:text-danger-400 mt-1">{{ error.file }}:{{ error.line }}</p>
                  </div>
                  <span class="text-xs text-danger-600 dark:text-danger-400">{{ error.count }}x</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recommendations -->
      <div v-if="recommendations.length > 0" class="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-primary-900 dark:text-primary-200 mb-4">Performance Recommendations</h3>
        <ul class="space-y-2">
          <li
            v-for="(rec, index) in recommendations"
            :key="index"
            class="flex items-start text-sm text-primary-800 dark:text-primary-300"
          >
            <CheckCircleIcon class="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{{ rec }}</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Info Modal -->
    <Modal :show="showInfo" @close="showInfo = false">
      <div class="p-6">
        <div class="flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mx-auto">
          <InformationCircleIcon class="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <h3 class="mt-4 text-lg font-medium text-secondary-900 dark:text-white text-center">
          Performance Monitoring
        </h3>
        <div class="mt-4 space-y-4 text-sm text-secondary-600 dark:text-secondary-400">
          <p>
            <strong class="text-secondary-900 dark:text-white">What is this page?</strong><br>
            Monitor your application's performance metrics, response times, database queries, and system health.
          </p>
          <p>
            <strong class="text-secondary-900 dark:text-white">Key Metrics:</strong><br>
            • <strong>Response Time:</strong> How fast your application responds to requests<br>
            • <strong>Database:</strong> Query performance and database health<br>
            • <strong>Cache Hit Rate:</strong> Effectiveness of caching (higher is better)<br>
            • <strong>Error Rate:</strong> Percentage of requests that fail
          </p>
          <p>
            <strong class="text-secondary-900 dark:text-white">Performance Goals:</strong><br>
            • Response time: &lt; 500ms (good), &lt; 1s (acceptable)<br>
            • Cache hit rate: &gt; 80%<br>
            • Error rate: &lt; 1%<br>
            • Database queries: &lt; 1s execution time
          </p>
        </div>
        <div class="mt-6">
          <button
            @click="showInfo = false"
            class="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 font-medium"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import HealthCard from '@/Components/Admin/HealthCard.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  InformationCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  health: {
    type: Object,
    required: true,
  },
  metrics: {
    type: Object,
    required: true,
  },
  dbMetrics: {
    type: Object,
    required: true,
  },
  queueMetrics: {
    type: Object,
    required: true,
  },
  cacheMetrics: {
    type: Object,
    required: true,
  },
  errorMetrics: {
    type: Object,
    required: true,
  },
  slowestEndpoints: {
    type: Array,
    default: () => [],
  },
  slowQueries: {
    type: Array,
    default: () => [],
  },
  recentErrors: {
    type: Array,
    default: () => [],
  },
  recommendations: {
    type: Array,
    default: () => [],
  },
});

const showInfo = ref(false);

const refreshMetrics = () => {
  router.reload({
    only: [
      'health',
      'metrics',
      'dbMetrics',
      'queueMetrics',
      'cacheMetrics',
      'errorMetrics',
      'slowestEndpoints',
      'slowQueries',
      'recentErrors',
      'recommendations',
    ],
  });
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US').format(num);
};
</script>
